import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { parse } from '../../../util/urlHelpers';
import { useIntl } from '../../../util/reactIntl';
import { hasCustomerVisibleStoreBlogs } from '../../../util/storeBlogs';
import css from './StorefrontTabs.module.css';

const TABS = {
    HOME: 'home',
    ABOUT: 'about',
    REVIEWS: 'reviews',
    GALLERY: 'gallery',
    BLOG: 'blog',
    FAQS: 'faqs',
};

const stripTrailingSlashes = value => (value || '').replace(/\/+$/, '');
const toPathname = value => {
    if (!value) return '';

    // If absolute URL is passed (e.g. http://localhost:3000/...), convert to pathname.
    if (/^https?:\/\//i.test(value)) {
        try {
            return new URL(value).pathname || '';
        } catch (e) {
            return value.replace(/^https?:\/\/[^/]+/i, '');
        }
    }

    return value;
};

const buildTabUrl = (storeUrl, tab) => {
    const normalized = toPathname(storeUrl || '');
    const [withoutHash] = normalized.split('#');
    const [base] = withoutHash.split('?');
    const cleanedBase = stripTrailingSlashes(base);
    return tab === TABS.HOME ? cleanedBase : `${cleanedBase}/${tab}`;
};

const hasNonEmptyArray = v => Array.isArray(v) && v.length > 0;
const hasNonEmptyString = v => typeof v === 'string' && v.trim().length > 0;

export const getActiveStorefrontTab = (locationPathname, locationSearch) => {
    const pathMatch = locationPathname?.match(/\/(about|reviews|gallery|blog|faqs)\/?$/);
    if (pathMatch?.[1] && Object.values(TABS).includes(pathMatch[1])) {
        return pathMatch[1];
    }
    const parsed = parse(locationSearch || '');
    const tab = typeof parsed.tab === 'string' ? parsed.tab : TABS.HOME;
    return Object.values(TABS).includes(tab) ? tab : TABS.HOME;
};

const StorefrontTabs = props => {
    const intl = useIntl();
    const {
        storeUrl,
        locationSearch,
        locationPathname, publicData = {},
        totalReviews = 0,
        isCurrentUser = false,
        storeBlogs = [],
        storeBlogsFetchCompleted = false,
    } = props;
    const location = useLocation();

    const currentPathname = locationPathname || location.pathname;
    const currentSearch = locationSearch != null ? locationSearch : location.search;
    const activeTab = getActiveStorefrontTab(currentPathname, currentSearch);

    const tabs = useMemo(() => {
        const hasGallery = hasNonEmptyArray(publicData.storeMedia);
        const hasFaqs = hasNonEmptyArray(publicData.storeFaqs);
        const hasAbout = hasNonEmptyString(publicData.storeAbout);
        const hasBlog = hasCustomerVisibleStoreBlogs(storeBlogs);
        const showBlogTab =
            isCurrentUser || (storeBlogsFetchCompleted && hasBlog);

        return [
            {
                key: TABS.HOME,
                label: intl.formatMessage({
                    id: 'StorefrontTabs.tab.rentals',
                    defaultMessage: 'Rentals',
                }),
                iconClass: 'ri-home-6-line',
                visible: true,
            },
            {
                key: TABS.ABOUT,
                label: intl.formatMessage({
                    id: 'StorefrontTabs.tab.about',
                    defaultMessage: 'About',
                }),
                iconClass: 'ri-store-3-line',
                visible: hasAbout || isCurrentUser,
            },
            {
                key: TABS.REVIEWS,
                label: intl.formatMessage({
                    id: 'StorefrontTabs.tab.reviews',
                    defaultMessage: 'Reviews',
                }),
                iconClass: 'ri-star-smile-line',
                visible: true,
                badge: typeof totalReviews === 'number' ? totalReviews : null,
            },
            {
                key: TABS.GALLERY,
                label: intl.formatMessage({
                    id: 'StorefrontTabs.tab.gallery',
                    defaultMessage: 'Gallery',
                }),
                iconClass: 'ri-gallery-view-2',
                visible: hasGallery || isCurrentUser,
            },
            {
                key: TABS.BLOG,
                label: intl.formatMessage({
                    id: 'StorefrontTabs.tab.blog',
                    defaultMessage: 'Blog',
                }),
                iconClass: 'ri-article-line',
                visible: showBlogTab,
            },
            {
                key: TABS.FAQS,
                label: intl.formatMessage({
                    id: 'StorefrontTabs.tab.faqs',
                    defaultMessage: 'FAQs',
                }),
                iconClass: 'ri-question-line',
                visible: hasFaqs || isCurrentUser,
            },
        ].filter(t => t.visible);
    }, [publicData, totalReviews, isCurrentUser, storeBlogs, storeBlogsFetchCompleted, intl]);

    return (
        <nav
            className={css.navWrap}
            aria-label={intl.formatMessage({
                id: 'StorefrontTabs.navigationAriaLabel',
                defaultMessage: 'Storefront navigation',
            })}
        >
            <div className={css.outer}>
                <div className={css.inner}>
                    {tabs.map(t => {
                        const isActive = activeTab === t.key;
                        const href = buildTabUrl(storeUrl, t.key);

                        return (
                            <Link
                                key={t.key}
                                to={href}
                                replace
                                className={classNames(css.tab, isActive ? css.active : null)}
                                aria-current={isActive ? 'page' : undefined}
                            >
                                <span className={css.icon} aria-hidden="true">
                                    <i className={t.iconClass} />
                                </span>
                                <span className={css.label}>{t.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
};

StorefrontTabs.propTypes = {
    storeUrl: PropTypes.string.isRequired,
    locationSearch: PropTypes.string,
    locationPathname: PropTypes.string,
    publicData: PropTypes.shape({
        storeMedia: PropTypes.array,
        storeFaqs: PropTypes.array,
        storeAbout: PropTypes.string,
    }),
    totalReviews: PropTypes.number,
    isCurrentUser: PropTypes.bool,
    storeBlogs: PropTypes.array,
    storeBlogsFetchCompleted: PropTypes.bool,
};

export default StorefrontTabs;
